import { gql, useLazyQuery } from '@apollo/client';
import * as React from 'react';

import { type TransactionFilters } from '../../../__generated__/globalTypes';
import { type CalendarDateRange } from '../../DateRangePicker';
import { type IEmptyTableStateProps } from '../helpers';

import type {
  HasTransactionsForUserQuery,
  HasTransactionsForUserQueryVariables,
} from './__generated__/useHasTransactionsForEmptyState.gql';

const HAS_TRANSACTIONS_FOR_USER = gql`
  query HasTransactionsForUser($filters: TransactionFilters) {
    hasTransactionsForUser(filters: $filters) {
      exists
      count
      startDate
      endDate
    }
  }
`;

interface IUseHasTransactionsForEmptyStateArgs {
  filters: TransactionFilters;
  isLoading: boolean;
  hasRows: boolean;
  currentDateRange?: CalendarDateRange;
  onViewTransactions: (range: CalendarDateRange) => void;
}

type HasTransactionsResult = NonNullable<HasTransactionsForUserQuery['hasTransactionsForUser']>;

const useHasTransactionsForEmptyState = ({
  filters,
  isLoading,
  hasRows,
  currentDateRange,
  onViewTransactions,
}: IUseHasTransactionsForEmptyStateArgs) => {
  const [checkHasTransactions, { data: hasTransactionsData, loading: hasTransactionsLoading }] = useLazyQuery<
    HasTransactionsForUserQuery,
    HasTransactionsForUserQueryVariables
  >(HAS_TRANSACTIONS_FOR_USER);

  React.useEffect(() => {
    if (!isLoading && !hasRows && (filters.startDate !== undefined || filters.endDate !== undefined)) {
      const filtersWithoutDate: TransactionFilters = {
        ...filters,
        startDate: undefined,
        endDate: undefined,
      };
      void checkHasTransactions({ variables: { filters: filtersWithoutDate } });
    }
  }, [checkHasTransactions, filters, hasRows, isLoading]);

  const hasTransactionsResult = React.useMemo<HasTransactionsResult | undefined>(() => {
    if (hasRows || isLoading || hasTransactionsLoading) {
      return undefined;
    }
    return hasTransactionsData?.hasTransactionsForUser ?? undefined;
  }, [hasRows, hasTransactionsData, hasTransactionsLoading, isLoading]);

  const emptyStateProps = React.useMemo<IEmptyTableStateProps>(
    () => ({
      hasTransactionsResult,
      currentDateRange,
      onViewTransactions,
    }),
    [hasTransactionsResult, currentDateRange, onViewTransactions]
  );

  return emptyStateProps;
};

export default useHasTransactionsForEmptyState;
